/**
 * EmailUnsubscribe: one-shot email unsubscribe landing page, rendered at
 * /email-unsubscribe/:token (public, no auth). On mount it calls api/user
 * (UserAPI.emailUnsubscribe) with the URL token and shows a success or error
 * message; components/LoadingSpinner is shown while the request is in flight.
 * Linked from unsubscribe links in outbound emails.
 */
import { UserAPI } from "@/api/user";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import LoadingSpinner from "@/components/LoadingSpinner";

type UnsubscribeStatus = "success" | "error";

const EmailUnsubscribe: React.FC = () => {
    const { token } = useParams();
    const [status, setStatus] = useState<UnsubscribeStatus | null>(null);
    const [message, setMessage] = useState<string>("");

    useEffect(() => {
        const unsubscribe = async () => {
            if (!token) {
                setStatus("error");
                setMessage("invalid unsubscribe link - no token provided");
                return;
            }

            try {
                const response = await UserAPI.emailUnsubscribe(token);
                setStatus("success");
                setMessage(response.message);
            } catch (error) {
                setStatus("error");
                setMessage(
                    typeof error === "string"
                        ? error
                        : "failed to unsubscribe. the link may be invalid or expired."
                );
            }
        };

        unsubscribe();
    }, [token]);

    const renderContent = () => {
        switch (status) {
            case "success":
                return (
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-slate-950 mb-8">success!</h2>
                        <p className="text-lg text-gray-700">{message}</p>
                    </div>
                );
            case "error":
                return (
                    <div className="text-center">
                        <h2 className="text-md font-bold text-slate-950 mb-8">{message}</h2>
                        <p className="text-sm text-gray-700 mt-4">
                            if you continue to receive emails, please contact support
                        </p>
                    </div>
                );
            default:
                return null;
        }
    };

    if (status === null) {
        return <LoadingSpinner />;
    }

    return (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-50 px-4 font-porsui overflow-hidden">
            <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
                <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">
                    unsubscribe from emails
                </h1>
                {renderContent()}
            </div>
        </div>
    );
};

export default EmailUnsubscribe;
